import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { opTime } from 'utils/dates';
import { getOpParams } from 'utils/getOpParams';

const MAILROOM_SOFTWARE_LICENSE_ID = 112;

/**
 * Determines if the banner should show based on the org being in an active mailroom trial and gathers information needed to show the banner.
 * @returns information required to render the banner and whether the banner should be shown.
 */
export const useTrialBanner = () => {
  const { orgId } = getOpParams();
  const { value: areTrialsEnabled, isLoading: isLoadingFeatureFlag } =
    useFeatureFlag('ENABLE_TRIALS', orgId);

  const { data: mailroomSoftwareLicense, isLoading: isLoadingSoftwareLicense } =
    useOpQuery({
      apiEndpointName: 'listOrgSoftwareLicenses',
      parameters: [
        orgId,
        { filter: `softwareLicenseId:${MAILROOM_SOFTWARE_LICENSE_ID}` },
      ],
      select: (heliumResponse) => (heliumResponse.json.data ?? [])[0],
      enabled: Boolean(areTrialsEnabled),
      suppressErrorMessage: true,
    });

  const { activatedAt, softwareLicenseOffer } = mailroomSoftwareLicense ?? {};

  const hasMailroomTrial = softwareLicenseOffer?.type === 'trial';

  const isLoading = isLoadingFeatureFlag || isLoadingSoftwareLicense;

  const billingStartsAt = opTime.utc(activatedAt).startOf('day'); // Keep the activatedAt date as is - not changed by timezone
  const now = opTime.utc().startOf('day');

  const daysLeft = billingStartsAt.diff(now, 'day');

  const bannerData = {
    billingStartsAt,
    daysLeft,
    isLoading,
  };

  /**
   * Don't show if trials are not enabled or
   * org has no active mailroom trial
   */
  if (!areTrialsEnabled || !hasMailroomTrial || isLoading) {
    return { ...bannerData, showBanner: false };
  }

  return { ...bannerData, showBanner: true };
};
